/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../app/nls/StringResources',
	'text!dashboard-core/js/lib/@waca/dashboard-common/dist/themes/themeListing.json'
], function(PropertiesProviderAPI, APIFactory, StringResources, themeListingJSON) {

	const CARBON_THEMES = ['carbonGrey10', 'carbonGrey100'];
	const LEGACY_THEMES = ['defaultTheme', 'lightTheme', 'darkTheme'];

	class DashboardPropertiesProvider {

		constructor(options) {
			this._api = APIFactory.createAPI(this, [PropertiesProviderAPI]);
			this._properties = options.features.Properties;
			this._boardModel = options.features.internal.getBoardModel();

			// register self as a properties provider contributing align properties
			this._properties.registerProvider(this._api);
			this._parseThemeListing();

			// TODO - we should not depend on the dashboard API in here
			this.dashboard = options.features.API;
			const featureChecker = this.dashboard && this.dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');

			this.registerEvents();
		}

		registerEvents() {
			this._boardModel.properties.on('change:fredIsRed', this._onChangeFredIsRed, this);
		}

		unregisterEvents() {
			this._boardModel.properties.off('change:fredIsRed', this._onChangeFredIsRed, this);
		}

		getAPI() {
			return this._api;
		}

		destroy() {
			if (this._properties) {
				this._properties.deregisterProvider(this._api);
			}
			this.unregisterEvents();
		}

		_onChangeFredIsRed({ value }) {
			this.dashboard.triggerDashboardEvent(
				'properties:setValue',
				{ propertyName: 'fredIsRed', value: !!value }
			);
		}

		getPropertyLayoutList(){
			if (this.isPropertiesV2Enabled) {
				return [{
					'value': StringResources.get('dashboardProperties'),
					'id': 'banner',
					'type': 'Banner',
					'editable': false
				}, {
					id: 'colorsAndTheme',
					type: 'Section',
					label: StringResources.get('sectionName_colorsAndThemes'),
					position: 3
				}];
			} else {
				const sections = [{
					'value': StringResources.get('dashboardProperties'),
					'id': 'banner',
					'type': 'Banner',
					'editable': false
				}, {
					id: 'general',
					type: 'Group',
					label: StringResources.get('tabName_general')
				}, {
					id: 'colorsAndTheme',
					type: 'Section',
					label: StringResources.get('sectionName_colorsAndThemes'),
					position: 3
				}];

				if (LEGACY_THEMES.includes(this._boardModel.get('theme'))) {
					sections[2]['sectionExtraMessage'] = StringResources.get('colorsAndThemes_legacyWarning');
				}

				return sections;
			}
		}

		getPropertyList() {
			const { properties } = this._boardModel;

			let sectionId_1, sectionId_2;
			if (this.isPropertiesV2Enabled) {
				sectionId_1 = 'colorsAndTheme';
				sectionId_2 = 'advanced';
			} else {
				sectionId_1 = 'general.colorsAndTheme';
				sectionId_2 = 'general.advanced';
			}

			return [
				{
					id: 'theme',
					setPropertyValue: (value, transactionToken) => {
						let localTransactionToken = transactionToken;
						let transaction = this.dashboard.getFeature('Transaction');
						if(!localTransactionToken){
							localTransactionToken = transaction.startTransaction();
						}

						const data = {
							undoRedoTransactionId: localTransactionToken.transactionId,
							transactionToken: localTransactionToken
						};

						const currentValue = this._boardModel.get('theme');
						if (currentValue === '') {
							this._boardModel.set({ 'theme': value }, { payloadData: data });
						} else {
							if (CARBON_THEMES.indexOf(value) !== -1) {
								//TODO Set this property -dashboardColorSet- via `setPropertyValue`
								properties.set({ 'dashboardColorSet': 'userPaletteColor' },  { payloadData: data });
								this._boardModel.set({ 'theme': value }, { payloadData: data });
							} else {
								this._boardModel.set({ 'theme': value }, { payloadData: data });
							}
						}

						if(!transactionToken){
							transaction.endTransaction(localTransactionToken);
						}
					},
					getPropertyValue: () =>  { return this._boardModel.get('theme'); },
					editor: {
						sectionId: sectionId_1,
						position: 1,
						uiControl: {
							type: 'DropDown',
							label: StringResources.get('propTheme'),
							getOptions: () => {
								const currentTheme = this._boardModel.get('theme');
								return this._themes.map(theme => {
									theme.selected = theme.value === currentTheme;
									return theme;
								}).filter(theme => {
									if (!theme.legacy || (theme.legacy && theme.selected)) {
										return theme;
									}
								});
							}
						}
					}
				},
				{
					id: 'dashboardColorSet',
					setPropertyValue: () =>  { throw new Error('dashboardColorSet should not be set'); },
					getPropertyValue: () => { return properties.get('dashboardColorSet') || 'userPaletteColor'; },
					editor: {
						hidden: true,
						sectionId: sectionId_1,
						position: 1,
						uiControl: {
							type: 'NewPalette',
							paletteType: 'ColorPalette',
							sectionLabel: StringResources.get('propDashboardColorPalette'),
							newPaletteLabel: StringResources.get('propNewDashboardPaletteLabel'),
							linkLabel: StringResources.get('propDashboardChangePaletteLink'),
							ariaLabel: StringResources.get('propDashboardColorPalette'),
							includeUserColorPalette: true,
							onChange: (propertyName, propertyValue) => {
								this.setPropertyValue(propertyName, propertyValue);
								this.dashboard.triggerDashboardEvent('properties:refreshPane', {
									sender: properties.id
								});
							}
						}
					}
				},
				{
					id: 'fredIsRed',
					setPropertyValue: value => { properties.set({ 'fredIsRed': value }); },
					getPropertyValue: () => { return properties.get('fredIsRed'); },
					validatePropertyValue: (value) => {
						return {
							isValid: typeof value === 'boolean',
							message: 'fredIsRed property must be of type boolean.'
						};
					},
					editor: {
						sectionId: sectionId_2,
						position: 1,
						uiControl: {
							type: 'ToggleButton',
							label: StringResources.get('propFredIsRed')
						}
					}
				}
			];
		}

		reset() { }

		_parseThemeListing() {
			if (!this._themes) {
				this._themes = [];
			}

			if (!this._themes.length) {
				const { themes } = JSON.parse(themeListingJSON);
				const legacyLabel = StringResources.get('legacyThemeLabel');

				themes.forEach(theme => {
					const label = theme.legacy ? legacyLabel : StringResources.get(`${theme.name}Label`);
					const themeData = {
						label,
						value: theme.name,
						legacy: theme.legacy || false
					};

					this._themes.push(themeData);
				});

				this._themes.sort((a,b) => a.order - b.order);
			}
		}
	}

	return DashboardPropertiesProvider;

});
